import * as React from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Button,
  Card,
  CardBody,
  Col,
  Row,
  Collapse,
  Table
} from 'reactstrap';
import { MdKeyboardArrowDown, MdKeyboardArrowUp, MdFileDownload } from 'react-icons/md';
import { FaCircle } from 'react-icons/fa';
import ThemeContext from '@app/providers/contexts/ThemeContext';
import Page from '@app/components/Page';
import Loader from '@app/components/Loader';
import './Reporting.css';
import { ReportsService, ReportsResponse, Site as ApiSite, Link as ApiLink } from '@app/api/reports/reports.service';

// Constants for report types
const REPORT_TYPE = {
  STARLINK_USAGE: 1
};

// Constants for export formats
const EXPORT_FORMAT = {
  CSV: 1
  // Add more formats here in the future
};

interface ThemeContextType {
  theme: string;
  setTheme: (theme: string) => void;
}

// Define interfaces for our component state (will be populated from API data)
interface Link {
  id: string;
  name: string;
  totalDataUsed: number; // in GB
  dataLimit: number; // in GB
  isOverLimit: boolean;
}

interface Site {
  id: string;
  name: string;
  clientCompany: string;
  links: Link[];
  totalDataUsed: number; // in GB
  isOverLimit: boolean;
}

interface ReportDetails {
  id: string;
  month: string;
  year: number;
  billingStart: string;
  billingEnd: string;
  sites: Site[];
  createdAt: string;
}

const StarlinkUsageReportDetailsView: React.FC = () => {
  const { reportId: urlReportId } = useParams<{ reportId: string }>();
  const history = useHistory();
  const { theme } = React.useContext(ThemeContext) as ThemeContextType;
  const [isLoading, setIsLoading] = React.useState(true);
  const [reportDetails, setReportDetails] = React.useState<ReportDetails | null>(null);
  const [expandedSites, setExpandedSites] = React.useState<{ [key: string]: boolean }>({});
  const [error, setError] = React.useState<string | null>(null);

  const getReportIdFromUrl = () => {
    const pathParts = window.location.pathname.split('/');
    const reportIndex = pathParts.findIndex(part => part === 'report');
    if (reportIndex >= 0 && reportIndex + 1 < pathParts.length) {
      return pathParts[reportIndex + 1];
    }
    return null;
  };

  const reportId = urlReportId || getReportIdFromUrl();

  // Toggle site expansion
  const toggleSite = (siteId: string) => {
    setExpandedSites(prevState => ({
      ...prevState,
      [siteId]: !prevState[siteId]
    }));
  };

  // Convert API data to our component format
  const mapApiReportToComponentFormat = (apiResponse: ReportsResponse, reportIndex: number = 0): ReportDetails => {
    // Make sure there's a report at the specified index
    if (!apiResponse.reports || !apiResponse.reports[reportIndex]) {
      throw new Error('No report found at the specified index');
    }
    
    const report = apiResponse.reports[reportIndex];
    
    // Parse the billing period start date
    // Make sure we properly handle the date format from API (YYYY-MM-DD)
    const startDateStr = report.billing_period.start;
    const [startYear, startMonth, startDay] = startDateStr.split('-').map(Number);
    
    // JavaScript months are 0-based, so we subtract 1 from the month
    const startDate = new Date(startYear, startMonth - 1, startDay);
    
    // Get month and year from start date for display
    const month = startDate.toLocaleString('default', { month: 'long' });
    const year = startDate.getFullYear();

    // Map sites and links
    const sites: Site[] = report.sites.map((apiSite: ApiSite) => {
      // Convert bytes to GB for display
      const totalDataUsedGB = apiSite.total_data_used / 1000000;

      // Map links within the site
      const links: Link[] = apiSite.links.map((apiLink: ApiLink) => {
        return {
          id: apiLink.link_id,
          name: apiLink.link_name,
          totalDataUsed: apiLink.data_used / 1000000, // Convert bytes to GB
          dataLimit: apiLink.data_limit / 1000000, // Convert bytes to GB
          isOverLimit: apiLink.over_limit
        };
      });

      return {
        id: apiSite.site_id,
        name: apiSite.site_name,
        clientCompany: apiSite.client_company,
        links: links,
        totalDataUsed: totalDataUsedGB,
        isOverLimit: apiSite.over_limit
      };
    });

    // Create the report detail object with the correct month and year
    const reportDetail = {
      id: report.id,
      month: month,
      year: year,
      billingStart: report.billing_period.start,
      billingEnd: report.billing_period.end,
      sites: sites,
      createdAt: new Date().toISOString() // Using current time as creation time
    };
    
    return reportDetail;
  };

  // Load report details
  React.useEffect(() => {
    const fetchReportDetails = async () => {
      setIsLoading(true);
      try {
        // Fetch the report with the appropriate report type
        const response = await ReportsService.getStarlinkUsageReport(
          undefined, 
          undefined,
          REPORT_TYPE.STARLINK_USAGE // Pass the report type
        );
        
        // If we have a specific reportId from the URL, try to find that report
        let reportIndex = 0;
        if (reportId) {
          const foundIndex = response.reports.findIndex(report => report.id === reportId);
          if (foundIndex >= 0) {
            reportIndex = foundIndex;
          }
        }
        
        // Convert API response to our component format
        const processedReport = mapApiReportToComponentFormat(response, reportIndex);
        
        setReportDetails(processedReport);
        setError(null);
      } catch (err: any) {
        console.error('Failed to load report details:', err);
        // Don't set error state for network errors
        if (!err.message || (err.message && !err.message.includes('Network Error'))) {
          setError(err.message || 'Failed to load report details');
        } else {
          // Just log network errors 
          // console.log('Network error occurred, suppressing error banner');
        }
        setReportDetails(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReportDetails();
  }, [reportId]);

  // Force document title update when report details change
  React.useEffect(() => {
    if (reportDetails) {
      document.title = `Starlink Usage Report: ${reportDetails.month} ${reportDetails.year}`;
    }
  }, [reportDetails]);

  const handleExportCSV = async () => {
    if (!reportDetails) {
      alert('No report data available to export');
      return;
    }

    try {
      // Call the export API to get the download URL with the report type
      const exportResponse = await ReportsService.exportStarlinkUsageReport(
        reportDetails.id,
        EXPORT_FORMAT.CSV, // Use constant instead of hardcoded value
        REPORT_TYPE.STARLINK_USAGE // Pass the report type
      );
      
      // Open the download URL in a new tab or trigger download
      if (exportResponse.url) {
        window.open(exportResponse.url, '_blank');
      } else {
        throw new Error('No download URL returned from the server');
      }
    } catch (err: any) {
      console.error('Export failed:', err);
      alert(`Failed to export report: ${err.message || 'Unknown error'}`);
    }
  };

  const formatDate = (dateString: string) => {
    // Handle YYYY-MM-DD format from API
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-').map(Number);
      // JavaScript months are 0-based, so we subtract 1 from the month
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    
    // Handle other date formats with time component
    const fullDateString = dateString.includes('T') ? dateString : `${dateString}T00:00:00Z`;
    const date = new Date(fullDateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusClass = (isOverLimit: boolean) => {
    return isOverLimit ? 'status-text-magenta' : 'status-text-green';
  };

  const getStatusText = (isOverLimit: boolean) => {
    return isOverLimit ? 'Over Limit' : 'Within Limit';
  };

  const getStatusIcon = (isOverLimit: boolean) => {
    return isOverLimit ? 
      <FaCircle className="status-text-magenta" size={14} /> : 
      <FaCircle className="status-text-green" size={14} />;
  };

  return (
    <>
      <Helmet>
        <title>Starlink Usage Report: {reportDetails ? `${reportDetails.month} ${reportDetails.year}` : 'Details'}</title>
      </Helmet>
      {isLoading ? (
        <Loader />
      ) : (
        <Page
          tag="div"
          className={`cr-page px-3 pt-2 controllers-list ${theme}`}
          title=""
          key={`report-${reportDetails?.id || 'loading'}`}
          breadcrumbs={[
            { name: 'Home', active: false, to: '/' },
            { name: 'Dashboard', active: false, to: '/dashboard' },
            { name: 'Reporting', active: false, to: '/reporting' },
            { name: 'Starlink Usage Reports', active: false, to: '/reporting/starlink-usage' },
            { name: reportDetails ? `${reportDetails.month} ${reportDetails.year}` : 'Report Details', active: true }
          ]}
          actionButton={null}
        >
          {error && error !== 'Network Error' && error.indexOf('Network Error') === -1 ? (
            <Row>
              <Col lg={12} md={12} sm={12} xs={12} className="mb-3">
                <Card className="p-3">
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                </Card>
              </Col>
            </Row>
          ) : null}

          {reportDetails ? (
            <>
              <Row>
                <Col lg={12} md={12} sm={12} xs={12} className="mb-2">
                  <Card className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <h2 className="mb-0">
                          <strong>Starlink Usage Report: {reportDetails.month} {reportDetails.year}</strong>
                        </h2>
                        <p className="text-muted mb-0">
                          Billing Period: <span className="fw-normal">{formatDate(reportDetails.billingStart)} - {formatDate(reportDetails.billingEnd)}</span>
                        </p>
                        <p className="text-muted">
                          Created: <span className="fw-normal">{formatDate(reportDetails.createdAt)}</span>
                        </p>
                      </div>
                      <Button 
                        className="ethica-button-green" 
                        onClick={handleExportCSV}
                      >
                        <MdFileDownload className="me-1" /> Export to CSV
                      </Button>
                    </div>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col lg={12} md={12} sm={12} xs={12}>
                  <Card className="p-3">
                      <table className="controllers-list-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                        <thead>
                          <tr>
                            <th>
                              <div className="d-flex align-items-center justify-content-start">
                                <strong>Site Name</strong>
                              </div>
                            </th>
                            <th>
                              <div className="d-flex align-items-center justify-content-start">
                                <strong>Company</strong>
                              </div>
                            </th>
                            <th>
                              <div className="d-flex align-items-center justify-content-start">
                                <strong>Number of Links</strong>
                              </div>
                            </th>
                            <th>
                              <div className="d-flex align-items-center justify-content-start">
                                <strong>Total Data Used (GB)</strong>
                              </div>
                            </th>
                            <th className="status-column">
                              <div className="d-flex align-items-center justify-content-center">
                                <strong>Overlimit</strong>
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportDetails.sites.map(site => (
                            <React.Fragment key={site.id}>
                              <tr 
                                className="cursor-pointer"
                                onClick={() => toggleSite(site.id)}
                              >
                                <td>
                                  <button onClick={(e) => {
                                    e.stopPropagation();
                                    toggleSite(site.id);
                                  }} className="table-arrow">
                                    {expandedSites[site.id] ? '▼' : '▶'}
                                  </button>
                                  {site.name}
                                </td>
                                <td style={{ textAlign: 'left' }}>{site.clientCompany}</td>
                                <td style={{ textAlign: 'left' }}>{site.links.length}</td>
                                <td style={{ textAlign: 'left' }}>{site.totalDataUsed.toFixed(2)} GB</td>
                                <td className="status-column">
                                  <div className="status-icon-container">
                                    {getStatusIcon(site.isOverLimit)}
                                  </div>
                                </td>
                              </tr>

                              {expandedSites[site.id] && (
                                <>
                                  <tr className="expanded-row-header">
                                    <td style={{paddingLeft: '34px', fontSize: '12px', fontWeight: '800'}}>Link Name</td>
                                    <td style={{fontSize: '12px', fontWeight: '800', textAlign: 'left'}}>Data Used (GB)</td>
                                    <td style={{fontSize: '12px', fontWeight: '800', textAlign: 'left'}}>Data Limit (GB)</td>
                                    <td></td>
                                    <td className="status-column text-center"></td>
                                  </tr>
                                  {site.links.map(link => (
                                    <tr key={link.id} className="expanded-row">
                                      <td style={{paddingLeft: '34px'}}>{link.name}</td>
                                      <td style={{textAlign: 'left'}}>{link.totalDataUsed.toFixed(2)}</td>
                                      <td style={{textAlign: 'left'}}>{link.dataLimit.toFixed(2)}</td>
                                      <td></td>
                                      <td className="status-column">
                                        <div className="status-icon-container">
                                          {getStatusIcon(link.isOverLimit)}
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </Card>
                  </Col>
                </Row>
            </>
          ) : !isLoading && !error && (
            <Row>
              <Col lg={12} md={12} sm={12} xs={12} className="mb-3">
                <Card className="p-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h2 className="mb-0"><strong>Starlink Usage Reports</strong></h2>
                  </div>
                  <table className="controllers-list-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th>Report Period</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td colSpan={3} className="text-center">No data available</td>
                      </tr>
                    </tbody>
                  </table>
                </Card>
              </Col>
            </Row>
          )}
        </Page>
      )}
    </>
  );
};

export default StarlinkUsageReportDetailsView; 